import { defineStore } from 'pinia';
import axios from 'axios';
import { useMainStore } from '@/stores/useMainStore';
import { reportSilentError, reportNonFatalError } from '@/utils/storeErrorReporting';
import { getAttachmentUploadError, getAttachmentDeleteError } from '@/utils/errorMessages';
import { API_DOMAIN, ENTITY_PLURAL_MAP } from '@/constants';

export const useAttachmentStore = defineStore('attachment', {
    state: () => ({}),

    getters: {
        /**
         * Returns the attachments array for a given entity type from the main store.
         * Follows the Pinia getter-returning-function pattern for parameterised access.
         * @returns {Function} (entityType: string) => Array
         */
        attachmentsFor() {
            return (entityType) => this.getEntityAttachments(entityType);
        },
    },

    actions: {
        // ─── Internal helpers ───────────────────────────────────────────────────

        getEntityAttachments(entityType) {
            return useMainStore().page[`${entityType}_attachments`] || [];
        },

        setEntityAttachments(entityType, attachments) {
            useMainStore().updatePage({ [`${entityType}_attachments`]: attachments });
        },

        /**
         * Returns the entity-scoped ID key for payload data objects.
         * e.g. 'request' → 'requestId'
         * @param {string} entityType
         * @returns {string}
         */
        entityIdKey(entityType) {
            return `${entityType}Id`;
        },

        /**
         * Returns the REST path segment for a given entity type.
         * e.g. 'request' → 'requests/123/attachments'
         * @param {string} entityType
         * @param {string} entityId
         * @returns {string}
         */
        attachmentsPath(entityType, entityId) {
            return `${API_DOMAIN}/${ENTITY_PLURAL_MAP[entityType]}/${entityId}/attachments`;
        },

        // ─── Generic Attachment Actions ─────────────────────────────────────────

        /**
         * Updates or adds an attachment in the entity's attachments array.
         * @param {Object} payload - Contains file object and status string
         * @param {string} entityType - 'request' | 'opportunity'
         */
        updateAttachment(payload, entityType) {
            const { file, status } = payload;
            const filename = file.filename || file.name;
            const attachments = [...this.getEntityAttachments(entityType)];

            const existingIdx = attachments.findIndex((a) => a.filename === filename);

            if (existingIdx > -1) {
                if (status === 'unstaged') {
                    attachments.splice(existingIdx, 1);
                } else {
                    attachments[existingIdx].status = status;
                }
            } else {
                const isOwner = status === 'staged';
                const newAttachment = {
                    filename,
                    name: file.name,
                    size: file.size,
                    type: file.type,
                    status,
                    is_owner: isOwner,
                };

                if (status === 'staged') {
                    newAttachment.rawFile = file;
                }

                attachments.push(newAttachment);
            }

            this.setEntityAttachments(entityType, attachments);
        },

        /**
         * Removes all staged attachments for a given entity type from the store.
         * Called when the form is cancelled or unmounted without saving.
         * @param {string} entityType - 'request' | 'opportunity'
         */
        clearStagedAttachments(entityType) {
            const current = this.getEntityAttachments(entityType);
            const committed = current.filter((a) => a.status !== 'staged');
            this.setEntityAttachments(entityType, committed);
        },

        /**
         * Uploads all staged attachments for a given entity.
         * Called after a confirmed save so files only reach S3 when the form is actually submitted.
         * @param {string} entityType - 'request' | 'opportunity'
         * @param {string} entityId - ID of the entity
         * @param {string} token - Auth token (reuse from the save payload)
         */
        async uploadStagedAttachments(entityType, entityId, token) {
            const staged = this.getEntityAttachments(entityType).filter((a) => a.status === 'staged' && a.rawFile);

            if (!staged.length) return;

            await Promise.all(staged.map((attachment) => this.uploadOneStagedAttachment(attachment, entityType, entityId, token)));
        },

        /**
         * Handles the full upload flow for a single staged attachment:
         * requests a presigned URL then streams the file to S3.
         * @param {Object} attachment - Staged attachment with rawFile
         * @param {string} entityType
         * @param {string} entityId
         * @param {string} token
         */
        async uploadOneStagedAttachment(attachment, entityType, entityId, token) {
            const file = attachment.rawFile;
            const uploadUrlPayload = { data: { [this.entityIdKey(entityType)]: entityId, file }, token };

            let presigned;
            try {
                presigned = await this.getAttachmentUploadUrl(uploadUrlPayload, entityType);
            } catch (error) {
                reportNonFatalError(error, getAttachmentUploadError());
                return;
            }

            const uploadPayload = { data: { presigned: presigned.data.presignedUrl, file }, token };
            await this.uploadAttachment(uploadPayload, entityType);
        },

        /**
         * Gets a presigned S3 URL for uploading an attachment to an entity.
         * @param {Object} payload - Contains token and data with entityId and file
         * @param {string} entityType - 'request' | 'opportunity'
         * @returns {Object} Response object with presigned URL data
         */
        async getAttachmentUploadUrl(payload, entityType) {
            const { token: accessToken, data } = payload;
            const entityId = data[this.entityIdKey(entityType)];
            const params = new URLSearchParams({ filename: data.file.name });

            try {
                return await axios.post(
                    `${this.attachmentsPath(entityType, entityId)}?${params}`,
                    {},
                    { headers: { Authorization: `Bearer ${accessToken}` } },
                );
            } catch (error) {
                this.updateAttachment({ file: { filename: data.file.name }, status: 'error' }, entityType);
                throw error;
            }
        },

        /**
         * Uploads a file to S3 using a presigned URL.
         * @param {Object} payload - Contains data with presigned object and file
         * @param {string} entityType - 'request' | 'opportunity'
         */
        async uploadAttachment(payload, entityType) {
            const { presigned, file } = payload.data;
            const formData = new FormData();

            Object.entries(presigned.fields).forEach(([key, value]) => {
                formData.append(key, value);
            });
            formData.append('file', file);

            try {
                await axios.post(presigned.url, formData);
                this.updateAttachment({ file: { filename: file.name }, status: 'success' }, entityType);
            } catch (error) {
                reportNonFatalError(error, getAttachmentUploadError());
                throw error;
            }
        },

        /**
         * Fetches all attachments for an entity.
         * @param {Object} payload - Contains token and data with entityId
         * @param {string} entityType - 'request' | 'opportunity'
         */
        async getAttachments(payload, entityType) {
            const { token: accessToken, data } = payload;
            const entityId = data[this.entityIdKey(entityType)];

            try {
                const result = await axios.get(this.attachmentsPath(entityType, entityId), {
                    headers: { Authorization: `Bearer ${accessToken}` },
                });

                const attachments = result.data.files;
                attachments.sort((a, b) => new Date(a.last_modified) - new Date(b.last_modified));

                this.setEntityAttachments(entityType, attachments);
            } catch (error) {
                reportSilentError(error);
            }
        },

        /**
         * Deletes an attachment from an entity.
         * @param {Object} payload - Contains token and data with entityId and filename
         * @param {string} entityType - 'request' | 'opportunity'
         * @returns {Promise} Result of the delete operation
         */
        async deleteAttachment(payload, entityType) {
            const { token: accessToken, data } = payload;
            const entityId = data[this.entityIdKey(entityType)];
            const params = new URLSearchParams({ filename: data.filename });

            try {
                const result = await axios.delete(`${this.attachmentsPath(entityType, entityId)}?${params}`, {
                    headers: { Authorization: `Bearer ${accessToken}` },
                });
                await this.getAttachments(payload, entityType);
                return result;
            } catch (error) {
                reportNonFatalError(error, getAttachmentDeleteError());
                throw error;
            }
        },
    },
});
